import { createAsyncThunk, createSlice,    PayloadAction } from "@reduxjs/toolkit";
import {
  dropdownLookup,
  requestCaptcha,
  submitCaptcha,
  validateSession,
} from "./API";
import { FormState } from "final-form";
import { DropDownOption } from "../models/DropDownOption";
import { ErrorResponse } from "../models/ErrorResponse";
import { CaptchaResponse, SubmitCaptchaResponse } from "../models/Captcha";
import { ValidateSessionResponse } from "../models/ValidateSession";
import { CaptchaDetailsForm } from "../models/CaptureDetailsForm";

export interface CaptureDetailsState {
  dropdownOptions: Array<DropDownOption>,
  dropdownsLoading: boolean,
  captchaAudio: string,
  captchaImage: string,
  detailsCaptured: boolean,
  captchaSuccessful: boolean,
  captchaSubmissionStarted: boolean,
  captureDetailsLoading: boolean;
  captureDetailsErrors: null | ErrorResponse;
  requesterDetailsFormState?: null | FormState<Record<string, any>, Partial<Record<string, any>>>;
  attendingAppointment: boolean,
  userCaptcha: string,
  isAgent: boolean
}

const initialState: CaptureDetailsState = {
  dropdownOptions: [],
  dropdownsLoading: false,
  captchaAudio: "",
  captchaImage: "",
  detailsCaptured: false, 
  captchaSuccessful: false,
  captchaSubmissionStarted: false,
  attendingAppointment: true,
  captureDetailsLoading: false,
  captureDetailsErrors: null,
  userCaptcha: "",
  isAgent: false
};

export const validateUserSession = createAsyncThunk<ValidateSessionResponse, void, { rejectValue:ErrorResponse}>(
  "captureDetails/validateSession",
  async (_, { rejectWithValue, fulfillWithValue, dispatch }) => {
    return validateSession()
      .then(async (res) => {
        await dispatch(getDropdownData(res.token ? res.token : ""));
        return fulfillWithValue(res);
      })
      .catch((e: ErrorResponse) => rejectWithValue(e));
  }
);

export const getDropdownData = createAsyncThunk<Array<DropDownOption>, string, { rejectValue:ErrorResponse}>(
  "captureDetails/getDropdownData",
  async (token, { rejectWithValue, fulfillWithValue }) => {
    return dropdownLookup(token)
      .then((res) => fulfillWithValue(res))
      .catch((e: ErrorResponse) => rejectWithValue(e));
  }
);

export const refreshCaptchaAsync = createAsyncThunk<CaptchaResponse,void,{ rejectValue:ErrorResponse}>(
  "captureDetails/refreshCaptcha",
  async (_, { rejectWithValue, fulfillWithValue }) => {
    return requestCaptcha()
      .then((res) => fulfillWithValue(res))
      .catch((e) => rejectWithValue(e));
  }
);

export const submitCaptchaAsync = createAsyncThunk<SubmitCaptchaResponse,void,{ state: {captureDetails: CaptureDetailsState},rejectValue:ErrorResponse}>(
  "captureDetails/submitCaptcha",
  async (_, { rejectWithValue, fulfillWithValue,getState }) => {
    return submitCaptcha(getState().captureDetails.userCaptcha)
      .then((res) => fulfillWithValue(res))
      .catch((e: ErrorResponse) => rejectWithValue(e));
  }
);

export const createDetailsSlice = createSlice({
  name: "captureDetails",
  initialState,
  reducers: 
  {
    updateRequesterDetailsFormState: (state: CaptureDetailsState, action) => {
      state.requesterDetailsFormState = {...action.payload};
    },
    setDetailsCaptured: (state: CaptureDetailsState, action) => {
      state.detailsCaptured = action.payload;
    },
    setUserCaptcha:(state: CaptureDetailsState, action)=>{
      state.userCaptcha = action.payload;
    },
    cancelCaptureDetails: (state: CaptureDetailsState, action) => {
      state.captureDetailsLoading = false;
      state.captureDetailsErrors = null;
    },
    clearCaptureDetailsErrors: (state: CaptureDetailsState, action) => {
      state.captureDetailsErrors = null;
    },
    setCaptureDetailsErrors: (state: CaptureDetailsState, action) => {
      state.captureDetailsErrors = action.payload;
    },
    setCaptureDetailsLoading: (state: CaptureDetailsState, action) => {
      state.captureDetailsLoading = action.payload;
    },
    setAttendingAppointment: (state: CaptureDetailsState, action) => {
      state.attendingAppointment = action.payload;
    },
    setCaptchaSubmissionStarted: (state: CaptureDetailsState, action) => {
      state.captchaSubmissionStarted = action.payload;
    },
    setCaptchaSuccessful: (state: CaptureDetailsState, action) => {
      state.captchaSuccessful = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateUserSession.fulfilled, (state: CaptureDetailsState, action: PayloadAction<ValidateSessionResponse>) => {
        if(action.payload.token && action.payload.token !== ""){
          sessionStorage.setItem("token", action.payload.token);
        }

        if(action.payload.agent) {
          state.isAgent = action.payload.agent;
          sessionStorage.setItem("agent", action.payload.agent ? "true" : "false");
        }
      })
      .addCase(validateUserSession.rejected, (state: CaptureDetailsState, action) => {
        state.captureDetailsErrors = (action as PayloadAction<ErrorResponse>).payload;
      })
      .addCase(getDropdownData.pending, (state: CaptureDetailsState) => {
        state.dropdownsLoading = true;
      })
      .addCase(getDropdownData.fulfilled, (state: CaptureDetailsState, action: PayloadAction<Array<DropDownOption> | ErrorResponse>) => {
        state.dropdownsLoading = false;
        state.dropdownOptions = (action as PayloadAction<Array<DropDownOption>>).payload;
      })
      .addCase(getDropdownData.rejected, (state: CaptureDetailsState, action) => {
        state.dropdownsLoading = false;
        state.captureDetailsErrors = (action as PayloadAction<ErrorResponse>).payload
      })
      .addCase(submitCaptchaAsync.fulfilled, (state: CaptureDetailsState, action: PayloadAction<SubmitCaptchaResponse>) => {
        state.captureDetailsLoading = false;
        state.captchaSuccessful = action.payload.captchaRes;
        state.captchaSubmissionStarted = false;
      })
      .addCase(submitCaptchaAsync.rejected, (state: CaptureDetailsState, action) => {
        state.captureDetailsErrors = (action as PayloadAction<ErrorResponse>).payload;
        state.captureDetailsLoading = false;
        state.captchaSuccessful = false;
        state.captchaSubmissionStarted = false;
      })
      .addCase(submitCaptchaAsync.pending, (state: CaptureDetailsState) => {
        state.captureDetailsLoading = true;
      })
      .addCase(refreshCaptchaAsync.fulfilled, (state: CaptureDetailsState, action: PayloadAction<CaptchaResponse>) => {
        state.captureDetailsLoading = false;
        state.captchaImage = action.payload.captchaImage;
        state.captchaAudio = action.payload.captchaAudio;
      })
      .addCase(refreshCaptchaAsync.rejected, (state: CaptureDetailsState, action) => {
        state.captureDetailsLoading = false;
        state.captureDetailsErrors = {
          fatal: state.captureDetailsErrors?.fatal ||(action as PayloadAction<ErrorResponse>).payload.fatal , 
          error: (action as PayloadAction<ErrorResponse>).payload.error , 
          errortitle: (action as PayloadAction<ErrorResponse>).payload.errortitle  
        }
      })
      .addCase(refreshCaptchaAsync.pending, (state: CaptureDetailsState) => {
        state.captureDetailsLoading = true;
      });
  },
});

export const {
  setDetailsCaptured,
  updateRequesterDetailsFormState,
  setAttendingAppointment,
  cancelCaptureDetails,
  clearCaptureDetailsErrors,
  setCaptureDetailsErrors,
  setCaptureDetailsLoading,
  setUserCaptcha,  
  setCaptchaSubmissionStarted,
  setCaptchaSuccessful
} = createDetailsSlice.actions;

export const detailsCaptured = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.detailsCaptured;
export const dropdownOptions = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.dropdownOptions;
export const dropdownsLoading = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.dropdownsLoading;
export const captchaAudio = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.captchaAudio;
export const captchaImage = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.captchaImage;
export const requesterDetailsFormState = (state: { captureDetails: CaptureDetailsState }) =>
  state?.captureDetails?.requesterDetailsFormState;
export const captchaSuccessful = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.captchaSuccessful;
export const attendingAppointment = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.attendingAppointment;
export const isCaptureDetailsLoading = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.captureDetailsLoading;
export const captureDetailsErrors = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.captureDetailsErrors;
export const userCaptcha = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.userCaptcha;
export const isAgent = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.isAgent;
export const captchaSubmissionStarted = (state: { captureDetails: CaptureDetailsState }) => state?.captureDetails?.captchaSubmissionStarted;

export default createDetailsSlice.reducer;
